import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import DevelopmentTierPanel from '../../../../../components/gamification/DevelopmentTierPanel';
import { ViewTabs, type InsightsView } from '../../Program/components/learning-lifecycle/AdminInsightsPanel';
import TeamActivitySections from '../../Program/components/learning-lifecycle/TeamActivitySections';
import { activitySections, learnerActivity } from '../../Program/components/learning-lifecycle/activityMeasures';
import MyCapabilityTab from './MyCapabilityTab';
import {
  useMyActivity,
  useMyActivityHistory,
  useMyCapability,
  useMyTrend,
  useRefreshCapability
} from '../../../../../queries/useCapability';
import { parseTierHistory } from '../../../../../components/gamification/scorecardData';
import { useAppSelector } from '../../../../../redux/hooks';
import { filterHiddenMemberAttributes } from '../../../utils/memberAttributes';
import { hasNoRole, initialRoleAnswers, roleTeams } from '../../../utils/teamRoles';
import { MOBILE_SX } from '../../../../../mobile/mobileApp';

type TierPanelProps = React.ComponentProps<typeof DevelopmentTierPanel>;

const BRANDY_FRAME = 'rgba(247, 148, 32, 0.45)';
const SURFACE = '#1C1C1C';

/**
 * The learner dashboard's left panel: the admin panel's Capability | Activity
 * tabs, about the learner themselves. Capability is their own score, chart and
 * measures; Activity is their tier, chart and the same five measures, with the
 * recency measures explained in days, not as a % of learners.
 */
const MemberInsightsPanel: React.FC<
  Pick<TierPanelProps, 'medals' | 'loading' | 'actionsByMeasure' | 'actionsLoading'> & {
    /** Phone: the dashboard's segmented control picks the view, so the panel drops its own tabs and frame. */
    view?: InsightsView;
  }
> = ({ medals, loading, actionsByMeasure, actionsLoading, view: hostView }) => {
  // Their levels on their role's capabilities, their trend, and their Roleplays and Practice Rounds.
  const { data: capability, isFetched } = useMyCapability();
  const { data: trend } = useMyTrend(Boolean(capability));
  const { data: extra, isError: extraFailed } = useMyActivity();
  // Last 6 Months: their Activity % at the end of each week (the backend rebuilds it).
  const { data: activityHistory } = useMyActivityHistory();
  const refresh = useRefreshCapability();
  // No role yet: which of the two things stands in the way. 'choose' while one of their teams still
  // waits for their answer (a role to pick on their profile); 'waiting' once they have answered "My
  // role isn't listed" (their role is still to be set up).
  const profile = useAppSelector((s) => s.userProfile.data);
  const lists = filterHiddenMemberAttributes((profile?.orgMemberAttributes ?? []) as any) as any;
  const selected = profile?.memberAttributes ?? [];
  const answers = initialRoleAnswers(profile?.teamRoles);
  const noRole = hasNoRole(profile?.teamRoles, lists, selected)
    ? roleTeams(profile?.teamRoles, lists, selected).some((team) => answers[team.teamValueId] === undefined)
      ? ('choose' as const)
      : ('waiting' as const)
    : null;
  // Open on Capability once there is one, or when they have no role (it says what to do); otherwise
  // Activity is the tab with content.
  const [view, setView] = useState<InsightsView>('tier');
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !isFetched) return;
    opened.current = true;
    if (capability || noRole) setView('gaps');
  }, [isFetched, capability, noRole]);

  const activityMedals = medals
    ? { ...medals, sections: activitySections(medals.sections, extra?.sections, 'learner') }
    : medals;
  const activity = learnerActivity(medals?.sections ?? [], extra?.sections);
  const shownView = hostView ?? view;

  return (
    <Box
      sx={{
        flex: '0 0 30%',
        minWidth: 300,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        mr: '8px',
        bgcolor: SURFACE,
        border: `1px solid ${BRANDY_FRAME}`,
        borderRadius: '8px',
        boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)',
        // Phone: the page scrolls, not the panel, and its sections are the cards.
        [MOBILE_SX]: {
          flex: 'none',
          minWidth: 0,
          height: 'auto',
          overflow: 'visible',
          mr: 0,
          bgcolor: 'transparent',
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none'
        }
      }}
    >
      {/* The same header row as the admin panel. */}
      <Box
        sx={{
          display: hostView ? 'none' : 'flex',
          alignItems: 'center',
          gap: '8px',
          px: '14px',
          py: '10px',
          minHeight: 56,
          borderBottom: '1px solid #2A2A2A',
          flexShrink: 0
        }}
      >
        <ViewTabs view={view} onChange={setView} learner />
      </Box>

      {shownView === 'gaps' ? (
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            scrollbarWidth: 'thin',
            scrollbarColor: '#565656 transparent',
            [MOBILE_SX]: { flex: 'none', overflowY: 'visible', p: '4px 0 0', gap: '12px' }
          }}
        >
          <MyCapabilityTab
            capability={capability ?? null}
            trend={trend}
            noRole={noRole}
            onChanged={() => refresh('mine')}
          />
        </Box>
      ) : (
        <DevelopmentTierPanel
          embedded
          sx={{ [MOBILE_SX]: { flex: 'none', p: '4px 0 0' } }}
          medals={activityMedals}
          loading={loading}
          actionsByMeasure={actionsByMeasure}
          actionsLoading={actionsLoading}
          intro={
            <TeamActivitySections
              activity={activity}
              history={activityHistory ? parseTierHistory(activityHistory.history) : undefined}
              audience="learner"
            />
          }
          leaders={extra?.leaders ?? (extraFailed ? null : undefined)}
        />
      )}
    </Box>
  );
};

export default MemberInsightsPanel;
